/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { WidgetV2 } from '../../../../../components/v2';
import PrioridadeProcedimentoChip from '../components/PrioridadeProcedimentoChip';

/** Procedimentos ligados ao tipo do serviço (`porTipoServico`); texto livre só tem os gerais. */
const documentosDoServico = (procedimentos, nsmpId, tipoServicoId) => {
  if (!tipoServicoId || !procedimentos) return [];
  if (procedimentos.nsmps?.[nsmpId]?.origem === 'TEXTO_LIVRE') return [];
  return (procedimentos.porTipoServico?.[tipoServicoId] || [])
    .filter((id) => procedimentos.procedimentos?.[id])
    .map((id) => ({ id, ...procedimentos.procedimentos[id] }));
};

const DocumentoLinha = ({ documento, onAbrir }) => (
  <ButtonBase
    onClick={() => onAbrir(documento.id)}
    aria-label={`Abrir o procedimento ${documento.titulo} no painel de apoio`}
    sx={{
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      px: 1.5,
      py: 0.75,
      textAlign: 'left',
      borderTop: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover',
      '&:hover': { bgcolor: 'action.selected' },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <MenuBookOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        Procedimento
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {documento.titulo}
      </Typography>
      <PrioridadeProcedimentoChip prioridade={documento.prioridade} />
    </Stack>
    <Stack direction="row" alignItems="center" sx={{ color: 'text.secondary', flexShrink: 0 }}>
      <Typography variant="caption">Abrir no painel</Typography>
      <ChevronRightIcon sx={{ fontSize: 18 }} />
    </Stack>
  </ButtonBase>
);

DocumentoLinha.propTypes = {
  documento: PropTypes.object.isRequired,
  onAbrir: PropTypes.func.isRequired,
};

const ServicoCard = ({ servico, documentos, onAbrirDocumento }) => {
  const semTipo = !servico.tipoServico;
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="subtitle2" sx={{ color: semTipo ? 'text.secondary' : 'text.primary' }}>
            {servico.tipoServico || 'Serviço sem tipo definido'}
          </Typography>
          {servico.prioridade && (
            <Chip size="small" variant="outlined" label={`Prioridade ${servico.prioridade}`} />
          )}
        </Stack>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, overflowWrap: 'anywhere', color: servico.descricao ? 'text.primary' : 'text.secondary' }}
        >
          {servico.descricao || 'Sem descrição do inspetor'}
        </Typography>
      </Box>
      {documentos.map((documento) => (
        <DocumentoLinha key={documento.id} documento={documento} onAbrir={onAbrirDocumento} />
      ))}
      {!documentos.length && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider' }}
        >
          {servico.tipoServicoId
            ? 'Sem procedimento cadastrado para este serviço.'
            : 'Sem tipo definido, não há procedimento vinculado. Valem os documentos gerais no painel.'}
        </Typography>
      )}
    </Box>
  );
};

ServicoCard.propTypes = {
  servico: PropTypes.object.isRequired,
  documentos: PropTypes.array.isRequired,
  onAbrirDocumento: PropTypes.func.isRequired,
};

const ServicosCampoSecao = ({ nsmpId, servicos, servicoTexto, procedimentos, onAbrirDocumento }) => {
  const lista = Array.isArray(servicos) ? servicos : [];
  const textoLivre = String(servicoTexto || '').trim();
  const itens = lista.map((servico) => ({
    servico,
    documentos: documentosDoServico(procedimentos, nsmpId, servico.tipoServicoId),
  }));
  const comDocumento = itens.filter((item) => item.documentos.length > 0).length;
  const subheader = lista.length
    ? `${lista.length} ${lista.length === 1 ? 'serviço' : 'serviços'} · ${comDocumento} com procedimento`
    : undefined;

  return (
    <WidgetV2 collapse title="Serviços levantados em campo" subheader={subheader}>
      <Stack spacing={1} sx={{ p: 2, bgcolor: 'background.default' }}>
        {itens.map((item, index) => (
          <ServicoCard
            key={item.servico.id || index}
            servico={item.servico}
            documentos={item.documentos}
            onAbrirDocumento={onAbrirDocumento}
          />
        ))}
        {!lista.length && textoLivre && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', px: 1.5, py: 1.25 }}>
            <Typography variant="subtitle2">Serviço (texto do inspetor)</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {textoLivre}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Sem tipo estruturado: só valem os procedimentos gerais da região.
            </Typography>
          </Box>
        )}
        {!lista.length && !textoLivre && (
          <Typography variant="body2" color="text.secondary">Nenhum serviço informado.</Typography>
        )}
      </Stack>
    </WidgetV2>
  );
};

ServicosCampoSecao.propTypes = {
  nsmpId: PropTypes.string,
  servicos: PropTypes.array,
  servicoTexto: PropTypes.string,
  procedimentos: PropTypes.object,
  onAbrirDocumento: PropTypes.func,
};

ServicosCampoSecao.defaultProps = {
  nsmpId: undefined,
  servicos: [],
  servicoTexto: '',
  procedimentos: null,
  onAbrirDocumento: () => {},
};

export default ServicosCampoSecao;
